import { z } from "zod";
import { DateOnlySchema, IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
const TIME_CATEGORIES = ["studies", "site", "meeting", "travel", "admin", "other"];
const TimeCategorySchema = z.enum(TIME_CATEGORIES);
const TIME_ENTRY_STATUSES = ["draft", "submitted", "approved", "rejected"];
const TimeEntryStatusSchema = z.enum(TIME_ENTRY_STATUSES);
const TIME_STEP_MINUTES = 15;
const CAPACITY_ALERT_BP = 11e3;
const WeekStartSchema = DateOnlySchema.refine(
  (d) => (/* @__PURE__ */ new Date(`${d}T12:00:00Z`)).getUTCDay() === 1,
  "a week starts on a Monday"
);
const MinutesSchema = z.int().min(TIME_STEP_MINUTES).max(1440).refine((m) => m % TIME_STEP_MINUTES === 0, "time is entered in 15-minute steps");
const TimeEntrySchema = z.strictObject({
  id: IdSchema,
  userId: IdSchema,
  userName: z.string().nullable(),
  projectId: IdSchema,
  projectName: z.string(),
  projectCode: z.string().nullable(),
  phaseId: IdSchema.nullable(),
  workDate: DateOnlySchema,
  weekStart: DateOnlySchema,
  minutes: z.int(),
  category: TimeCategorySchema,
  note: z.string().nullable(),
  status: TimeEntryStatusSchema,
  rejectionReason: z.string().nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: IsoDateTimeSchema.nullable(),
  version: z.int().min(1)
});
const MyWeekSchema = z.strictObject({
  weekStart: DateOnlySchema,
  /** Submitted: the draft and rejected entries became submitted; nothing is added until a rejection. */
  submittedAt: IsoDateTimeSchema.nullable(),
  entries: z.array(TimeEntrySchema).max(500),
  totalMinutes: z.int().min(0),
  /** Contract time of the week minus holidays and leave (null: no holiday calendar for the year). */
  capacityMinutes: z.int().min(0).nullable(),
  /** The public holidays of the week (YYYY-MM-DD and the i18n key under common:holidays). */
  holidays: z.array(
    z.strictObject({ date: DateOnlySchema, key: z.string(), estimated: z.boolean() })
  ),
  leaveDays: z.array(DateOnlySchema)
});
const MyWeekQuerySchema = z.strictObject({ week: WeekStartSchema.optional() });
const CreateTimeEntryRequestSchema = z.strictObject({
  projectId: IdSchema,
  phaseId: IdSchema.nullable().optional(),
  workDate: DateOnlySchema,
  minutes: MinutesSchema,
  category: TimeCategorySchema,
  note: z.string().trim().max(500).nullable().optional()
});
const UpdateTimeEntryRequestSchema = z.strictObject({
  phaseId: IdSchema.nullable(),
  workDate: DateOnlySchema,
  minutes: MinutesSchema,
  category: TimeCategorySchema,
  note: z.string().trim().max(500).nullable()
}).partial().refine((b) => Object.keys(b).length > 0, "send at least one field to change");
const TimeEntryParamsSchema = z.strictObject({ orgId: IdSchema, entryId: IdSchema });
const WeekParamsSchema = z.strictObject({ orgId: IdSchema, weekStart: WeekStartSchema });
const ApprovalQueueSchema = z.strictObject({
  weekStart: DateOnlySchema.nullable(),
  entries: z.array(TimeEntrySchema).max(1e3)
});
const ApprovalQueueQuerySchema = z.strictObject({ week: WeekStartSchema.optional() });
const DecideTimeEntriesRequestSchema = z.strictObject({
  entryIds: z.array(IdSchema).min(1).max(200),
  decision: z.enum(["approve", "reject"]),
  reason: z.string().trim().min(3).max(500).optional()
}).refine((b) => b.decision === "approve" || b.reason !== void 0, {
  message: "a rejection needs a reason",
  path: ["reason"]
});
const DecisionResultSchema = z.strictObject({ decided: z.int().min(0) });
const LEAVE_KINDS = ["leave", "sick", "training", "other"];
const LeaveKindSchema = z.enum(LEAVE_KINDS);
const LeaveEntrySchema = z.strictObject({
  id: IdSchema,
  userId: IdSchema,
  userName: z.string().nullable(),
  startsOn: DateOnlySchema,
  endsOn: DateOnlySchema,
  kind: LeaveKindSchema,
  note: z.string().nullable()
});
const LeaveListSchema = z.strictObject({ items: z.array(LeaveEntrySchema).max(500) });
const LeaveQuerySchema = z.strictObject({
  from: DateOnlySchema,
  to: DateOnlySchema,
  userId: IdSchema.optional()
});
const CreateLeaveRequestSchema = z.strictObject({
  userId: IdSchema.optional(),
  startsOn: DateOnlySchema,
  endsOn: DateOnlySchema,
  kind: LeaveKindSchema,
  note: z.string().trim().max(200).nullable().optional()
}).refine((b) => b.endsOn >= b.startsOn, {
  message: "the end is before the start",
  path: ["endsOn"]
});
const LeaveParamsSchema = z.strictObject({ orgId: IdSchema, leaveId: IdSchema });
const CapacityCellSchema = z.strictObject({
  weekStart: DateOnlySchema,
  capacityMinutes: z.int().min(0).nullable(),
  loadMinutes: z.int().min(0),
  /** Load over capacity in basis points (null without capacity). */
  loadBp: z.int().min(0).nullable(),
  /** Above 110 %. */
  overloaded: z.boolean(),
  byProject: z.array(
    z.strictObject({ projectId: IdSchema, projectName: z.string(), minutes: z.int().min(0) })
  )
});
const CapacityRowSchema = z.strictObject({
  userId: IdSchema,
  userName: z.string(),
  weeklyMinutes: z.int().min(0),
  weeks: z.array(CapacityCellSchema)
});
const CapacityBoardSchema = z.strictObject({
  from: DateOnlySchema,
  weeks: z.int().min(1).max(12),
  rows: z.array(CapacityRowSchema).max(500),
  /** Years of the range without a Tunisian holiday calendar (capacity then ignores holidays). */
  missingHolidayYears: z.array(z.int())
});
const CapacityQuerySchema = z.strictObject({
  from: WeekStartSchema,
  weeks: z.coerce.number().int().min(1).max(12).default(4)
});
const WorkingTimeRequestSchema = z.strictObject({
  weeklyMinutes: z.int().min(0).max(4200)
});
const WorkingTimeSchema = z.strictObject({
  memberId: IdSchema,
  weeklyMinutes: z.int()
});
export {
  ApprovalQueueQuerySchema,
  ApprovalQueueSchema,
  CAPACITY_ALERT_BP,
  CapacityBoardSchema,
  CapacityCellSchema,
  CapacityQuerySchema,
  CapacityRowSchema,
  CreateLeaveRequestSchema,
  CreateTimeEntryRequestSchema,
  DecideTimeEntriesRequestSchema,
  DecisionResultSchema,
  LEAVE_KINDS,
  LeaveEntrySchema,
  LeaveKindSchema,
  LeaveListSchema,
  LeaveParamsSchema,
  LeaveQuerySchema,
  MyWeekQuerySchema,
  MyWeekSchema,
  TIME_CATEGORIES,
  TIME_ENTRY_STATUSES,
  TIME_STEP_MINUTES,
  TimeCategorySchema,
  TimeEntryParamsSchema,
  TimeEntrySchema,
  TimeEntryStatusSchema,
  UpdateTimeEntryRequestSchema,
  WeekParamsSchema,
  WeekStartSchema,
  WorkingTimeRequestSchema,
  WorkingTimeSchema
};
//# sourceMappingURL=timesheets.js.map